/** Internal compute helpers for today list reorder blast-radius logic. */
import { generateKeyBetween } from 'fractional-indexing';
import {
  timeToMinutes,
  minutesToTime,
  taskDurationMinutes,
  endMinutesForTask,
} from '@/features/today/utils/todayListReorderTime';

/** @param {import('@/types').Task} a @param {import('@/types').Task} b */
const compareOrderKey = (a, b) => {
  const keyA = a.orderKey || '';
  const keyB = b.orderKey || '';
  return keyA < keyB ? -1 : keyA > keyB ? 1 : 0;
};

/** @param {import('@/types').Task[]} tasks */
export const buildTodayDisplayOrder = (tasks) => {
  const timed = tasks
    .filter((task) => task.time)
    .sort((a, b) => {
      const diff = timeToMinutes(a.time) - timeToMinutes(b.time);
      return diff !== 0 ? diff : compareOrderKey(a, b);
    });
  const flex = tasks.filter((task) => !task.time).sort(compareOrderKey);
  return [...timed, ...flex];
};

/**
 * @param {import('@/types').Task[]} tasks
 * @param {string} activeId
 * @param {string} overId
 */
export const reorderByActiveOver = (tasks, activeId, overId) => {
  const activeIdx = tasks.findIndex((task) => task.id === activeId);
  const overIdx = tasks.findIndex((task) => task.id === overId);
  if (activeIdx === -1 || overIdx === -1 || activeIdx === overIdx) return null;

  const next = [...tasks];
  const [activeItem] = next.splice(activeIdx, 1);
  const newOverIdx = next.findIndex((task) => task.id === overId);
  const insertAt = activeIdx < overIdx ? newOverIdx + 1 : newOverIdx;
  next.splice(insertAt, 0, activeItem);

  return { order: next, oldIndex: activeIdx, newIndex: insertAt };
};

/** @param {import('@/types').Task[]} timedTasks */
export const getDayAnchorTime = (timedTasks) => timedTasks[0]?.time ?? null;

/**
 * @param {import('@/types').Task[]} timedTasks
 * @param {number} blastStart
 * @param {number} blastEnd
 * @param {string} dayAnchorTime
 */
export const repackTimedBlast = (
  timedTasks,
  blastStart,
  blastEnd,
  dayAnchorTime,
) => {
  if (!dayAnchorTime || blastStart > blastEnd) return [];

  const updates = [];
  let prevEnd =
    blastStart === 0
      ? timeToMinutes(dayAnchorTime)
      : endMinutesForTask(timedTasks[blastStart - 1]);

  for (let i = blastStart; i <= blastEnd; i += 1) {
    const newTime = minutesToTime(prevEnd);
    if (timedTasks[i].time !== newTime) {
      updates.push({ id: timedTasks[i].id, time: newTime });
    }
    prevEnd += taskDurationMinutes(timedTasks[i]);
  }

  return updates;
};

/** @param {import('@/types').Task[]} newOrder @param {string} activeId @param {string} overId */
const isPromotedToTimed = (newOrder, activeId, overId) => {
  const activeIdx = newOrder.findIndex((task) => task.id === activeId);
  const overIdx = newOrder.findIndex((task) => task.id === overId);
  if (activeIdx === -1 || overIdx === -1) return false;
  if (!newOrder[overIdx]?.time) return false;
  return activeIdx < overIdx;
};

/** @param {import('@/types').Task[]} flexTasks @param {string} activeId @param {string} overId */
export const computeFlexFlexUpdates = (flexTasks, activeId, overId) => {
  const result = reorderByActiveOver(flexTasks, activeId, overId);
  if (!result) return [];

  const { order, newIndex } = result;
  const prev = order[newIndex - 1];
  const nextTask = order[newIndex + 1];
  const orderKey = generateKeyBetween(
    prev?.orderKey ?? null,
    nextTask?.orderKey ?? null,
  );
  const current = flexTasks.find((task) => task.id === activeId);
  if (!current || current.orderKey === orderKey) return [];
  return [{ id: activeId, orderKey }];
};

/** @param {import('@/types').Task[]} newOrder @param {string} activeId @param {import('@/types').Task} active */
export const computeTimedToFlexUpdates = (newOrder, activeId, active) => {
  const flexOrder = newOrder
    .map((task) => (task.id === activeId ? { ...task, time: null } : task))
    .filter((task) => !task.time);
  const idx = flexOrder.findIndex((task) => task.id === activeId);
  const prev = flexOrder[idx - 1];
  const nextTask = flexOrder[idx + 1];
  const orderKey = generateKeyBetween(
    prev?.orderKey ?? null,
    nextTask?.orderKey ?? null,
  );

  /** @type {{ id: string, time: null, orderKey?: string }} */
  const update = { id: activeId, time: null };
  if (orderKey !== active.orderKey) update.orderKey = orderKey;
  return [update];
};

/** @param {import('@/types').Task[]} newOrder @param {string} activeId @param {string} dayAnchor */
export const computeFlexToTimedUpdates = (newOrder, activeId, dayAnchor) => {
  const newTimed = [];
  for (const task of newOrder) {
    if (task.time) newTimed.push({ ...task });
    else if (task.id === activeId) newTimed.push({ ...task, time: null });
  }

  const newIdx = newTimed.findIndex((task) => task.id === activeId);
  let blastStart = newIdx;
  let blastEnd = newIdx;
  if (newIdx === 0 && newTimed.length > 1) blastEnd = 1;

  return repackTimedBlast(newTimed, blastStart, blastEnd, dayAnchor);
};

/** @param {import('@/types').Task[]} originalTimed @param {import('@/types').Task[]} newTimed @param {number} oldIdx @param {number} newIdx @param {string} dayAnchor */
export const computeTimedTimedUpdates = (
  originalTimed,
  newTimed,
  oldIdx,
  newIdx,
  dayAnchor,
) => {
  if (newTimed.length !== originalTimed.length) return [];
  const blastStart = Math.min(oldIdx, newIdx);
  const blastEnd = Math.max(oldIdx, newIdx);
  return repackTimedBlast(newTimed, blastStart, blastEnd, dayAnchor);
};

/**
 * @param {object} params
 * @param {boolean} params.activeWasTimed
 * @param {import('@/types').Task[]} params.originalTimed
 * @param {import('@/types').Task[]} params.originalFlex
 * @param {import('@/types').Task[]} params.newOrder
 * @param {string} params.activeId
 * @param {string} params.overId
 * @param {string | null} params.dayAnchor
 * @returns {Array<{ id: string, time?: string | null, orderKey?: string }> | null}
 */
export const resolveTimedReorderUpdates = ({
  activeWasTimed,
  originalTimed,
  originalFlex,
  newOrder,
  activeId,
  overId,
  dayAnchor,
}) => {
  if (
    !activeWasTimed &&
    (!dayAnchor || !isPromotedToTimed(newOrder, activeId, overId))
  ) {
    return computeFlexFlexUpdates(originalFlex, activeId, overId);
  }

  if (
    !activeWasTimed &&
    dayAnchor &&
    isPromotedToTimed(newOrder, activeId, overId)
  ) {
    return computeFlexToTimedUpdates(newOrder, activeId, dayAnchor);
  }

  const newTimed = newOrder.filter((task) => task.time);
  const oldTimedIdx = originalTimed.findIndex((task) => task.id === activeId);
  const newTimedIdx = newTimed.findIndex((task) => task.id === activeId);
  if (oldTimedIdx === -1 || newTimedIdx === -1 || !dayAnchor) return [];

  return computeTimedTimedUpdates(
    originalTimed,
    newTimed,
    oldTimedIdx,
    newTimedIdx,
    dayAnchor,
  );
};
